// src/platform/ui/installPrompt.js

import { ref, readonly } from 'vue'

import {
  eventBus,
  logger
} from '@/platform/core'

import installManager from '@/platform/pwa/installManager'

class InstallPrompt {
  constructor() {
    this.visible = ref(false)
    this.installing = ref(false)

    this.canInstall = ref(false)
    this.isInstalled = ref(false)

    this.platform = ref(
      this.detectPlatform()
    )

    this.initialized = false

    this.unsubscribeStatus = null
    this.unsubscribeAvailable = null
    this.unsubscribePrompted = null
    this.unsubscribeAccepted = null
    this.unsubscribeDismissed = null
    this.unsubscribeCompleted = null
    this.unsubscribeUnavailable = null
    this.unsubscribeError = null
  }

  init() {
    if (this.initialized) {
      return
    }

    this.unsubscribeStatus =
      eventBus.on(
        'platform.install.status',
        event => {
          this.handleStatus(event)
        }
      )

    this.unsubscribeAvailable =
      eventBus.on(
        'platform.install.available',
        event => {
          this.handleAvailable(event)
        }
      )

    this.unsubscribePrompted =
      eventBus.on(
        'platform.install.prompted',
        () => {
          this.handlePrompted()
        }
      )

    this.unsubscribeAccepted =
      eventBus.on(
        'platform.install.accepted',
        () => {
          this.handleAccepted()
        }
      )

    this.unsubscribeDismissed =
      eventBus.on(
        'platform.install.dismissed',
        () => {
          this.handleDismissed()
        }
      )

    this.unsubscribeCompleted =
      eventBus.on(
        'platform.install.completed',
        event => {
          this.handleCompleted(event)
        }
      )

    this.unsubscribeUnavailable =
      eventBus.on(
        'platform.install.unavailable',
        event => {
          this.handleUnavailable(event)
        }
      )

    this.unsubscribeError =
      eventBus.on(
        'platform.install.error',
        event => {
          this.handleError(event)
        }
      )

    const status =
      installManager.getStatus()

    this.canInstall.value =
      status.canInstall

    this.isInstalled.value =
      status.isInstalled

    this.initialized = true

    logger.info(
      'Install prompt initialized.',
      {
        platform:
          this.platform.value,

        canInstall:
          this.canInstall.value,

        isInstalled:
          this.isInstalled.value
      }
    )
  }

  destroy() {
    if (!this.initialized) {
      return
    }

    const subscriptions = [
      'unsubscribeStatus',
      'unsubscribeAvailable',
      'unsubscribePrompted',
      'unsubscribeAccepted',
      'unsubscribeDismissed',
      'unsubscribeCompleted',
      'unsubscribeUnavailable',
      'unsubscribeError'
    ]

    subscriptions.forEach(name => {
      if (this[name]) {
        this[name]()
        this[name] = null
      }
    })

    this.initialized = false

    logger.info(
      'Install prompt destroyed.'
    )
  }

  handleStatus(event) {
    if (!event) {
      return
    }

    this.canInstall.value =
      Boolean(event.canInstall)

    this.isInstalled.value =
      Boolean(event.isInstalled)
  }

  handleAvailable(event) {
    this.canInstall.value =
      Boolean(event?.canInstall)

    this.isInstalled.value =
      Boolean(event?.isInstalled)

    logger.info(
      'Install prompt received installation availability.'
    )
  }

  handlePrompted() {
    this.installing.value = true
  }

  handleAccepted() {
    this.installing.value = true
    this.visible.value = false

    logger.info(
      'Application install was accepted.'
    )
  }

  handleDismissed() {
    this.installing.value = false
    this.canInstall.value = false
    this.visible.value = false

    logger.info(
      'Application install was dismissed.'
    )
  }

  handleCompleted(event) {
    this.canInstall.value =
      Boolean(event?.canInstall)

    this.isInstalled.value =
      Boolean(event?.isInstalled)

    this.installing.value = false
    this.visible.value = false

    logger.info(
      'Application installation completed.'
    )
  }

  handleUnavailable(event) {
    this.installing.value = false

    logger.warn(
      'Application install prompt is unavailable.',
      {
        reason:
          event?.reason || null
      }
    )
  }

  handleError(event) {
    this.installing.value = false

    logger.error(
      'Install prompt received an installation error.',
      {
        error:
          event?.error || null
      }
    )
  }

  open() {
    if (this.isInstalled.value) {
      return false
    }

    if (
      this.canInstall.value ||
      this.isIOS()
    ) {
      this.visible.value = true

      return true
    }

    return false
  }

  close() {
    if (this.installing.value) {
      return false
    }

    this.visible.value = false

    return true
  }

  remindLater() {
    return this.close()
  }

  async install() {
    if (this.installing.value) {
      return {
        success: false,
        reason: 'install-in-progress'
      }
    }

    if (this.isInstalled.value) {
      return {
        success: false,
        reason: 'already-installed'
      }
    }

    /*
     * iOS Safari does not expose the
     * beforeinstallprompt API.
     */
    if (
      this.isIOS() &&
      !this.canInstall.value
    ) {
      return {
        success: false,
        reason:
          'manual-ios-install-required'
      }
    }

    this.installing.value = true

    try {
      const result =
        await installManager.install()

      if (!result.success) {
        this.installing.value = false
      }

      return result
    } catch (error) {
      this.installing.value = false

      logger.error(
        'Install prompt failed to initiate installation.',
        {
          error
        }
      )

      return {
        success: false,
        reason: 'install-error',
        error
      }
    }
  }

  refreshStatus() {
    const status =
      installManager.getStatus()

    this.canInstall.value =
      status.canInstall

    this.isInstalled.value =
      status.isInstalled

    return status
  }

  detectPlatform() {
    if (typeof navigator === 'undefined') {
      return 'unknown'
    }

    const userAgent =
      navigator.userAgent
        ?.toLowerCase() || ''

    const platform =
      navigator.platform
        ?.toLowerCase() || ''

    if (
      /iphone|ipad|ipod/.test(
        userAgent
      ) ||
      (
        platform === 'macintel' &&
        navigator.maxTouchPoints > 1
      )
    ) {
      return 'ios'
    }

    if (/android/.test(userAgent)) {
      return 'android'
    }

    if (/windows/.test(userAgent)) {
      return 'windows'
    }

    if (
      /macintosh|mac os x/.test(
        userAgent
      )
    ) {
      return 'macos'
    }

    if (/linux/.test(userAgent)) {
      return 'linux'
    }

    return 'unknown'
  }

  isIOS() {
    return (
      this.platform.value === 'ios'
    )
  }

  isAndroid() {
    return (
      this.platform.value === 'android'
    )
  }

  isWindows() {
    return (
      this.platform.value === 'windows'
    )
  }

  getInstructions() {
    if (this.isInstalled.value) {
      return {
        type: 'installed',

        title:
          'NexgbitsIS is installed',

        steps: []
      }
    }

    if (this.isIOS()) {
      return {
        type: 'manual',

        title:
          'Install NexgbitsIS',

        steps: [
          'Open NexgbitsIS in Safari.',
          'Tap the Share button.',
          'Select Add to Home Screen.',
          'Tap Add.'
        ]
      }
    }

    if (this.canInstall.value) {
      return {
        type: 'native',

        title:
          'Install NexgbitsIS',

        steps: []
      }
    }

    return {
      type: 'unavailable',

      title:
        'Installation unavailable',

      steps: []
    }
  }

  getState() {
    return {
      visible:
        readonly(this.visible),

      installing:
        readonly(this.installing),

      canInstall:
        readonly(this.canInstall),

      isInstalled:
        readonly(this.isInstalled),

      platform:
        readonly(this.platform)
    }
  }
}

const installPrompt =
  new InstallPrompt()

export default installPrompt